import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeftIcon, StoreIcon, DownloadIcon, MapPinIcon, BuildingIcon, CreditCardIcon, ActivityIcon, SettingsIcon, FileTextIcon, RefreshCwIcon, SmartphoneIcon, GlobeIcon } from 'lucide-react';
import { formatMoney } from '../../../shared/lib/money';
import { outletsData } from '../mocks/outletsData';
import { OutletDateRange } from '../components/OutletDateRange';
import { KpiCard } from '../../analytics/components/KpiCard';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
export function OutletDetail() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const {
    t
  } = useTranslation('common');
  const [activeTab, setActiveTab] = useState('overview');
  const outlet = outletsData.find(o => o.id === id);
  if (!outlet) {
    return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 text-center" data-id="element-7550">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-7551">Outlet not found</h2>
        <button onClick={() => navigate('/outlets/directory')} className="mt-4 text-blue-600 hover:text-blue-800" data-id="element-7552">
          Return to directory
        </button>
      </div>;
  }
  const tabs = [{
    id: 'overview',
    label: 'Overview',
    icon: ActivityIcon
  }, {
    id: 'payments',
    label: 'Payments',
    icon: CreditCardIcon
  }, {
    id: 'channels',
    label: 'Channels',
    icon: SmartphoneIcon
  }, {
    id: 'reconciliation',
    label: 'Reconciliation',
    icon: RefreshCwIcon
  }, {
    id: 'reports',
    label: 'Reports',
    icon: FileTextIcon
  }, {
    id: 'settings',
    label: 'Settings',
    icon: SettingsIcon
  }];
  const mockSalesData = [{
    date: 'Mon',
    sales: outlet.periodGmv * 0.1
  }, {
    date: 'Tue',
    sales: outlet.periodGmv * 0.12
  }, {
    date: 'Wed',
    sales: outlet.periodGmv * 0.15
  }, {
    date: 'Thu',
    sales: outlet.periodGmv * 0.14
  }, {
    date: 'Fri',
    sales: outlet.periodGmv * 0.18
  }, {
    date: 'Sat',
    sales: outlet.periodGmv * 0.2
  }, {
    date: 'Sun',
    sales: outlet.periodGmv * 0.11
  }];
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Onboarding':
        return 'bg-blue-100 text-blue-800';
      case 'Suspended':
        return 'bg-red-100 text-red-800';
      case 'Closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-7553">
      {/* Header */}
      <div className="mb-6" data-id="element-7554">
        <button onClick={() => navigate('/outlets/directory')} className="flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4 transition-colors" data-id="element-7555">
          <ArrowLeftIcon size={16} className="mr-1" data-id="element-7556" />
          Back to Directory
        </button>

        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-7557">
          <div className="flex items-start gap-4" data-id="element-7558">
            <div className="w-16 h-16 bg-white border border-gray-200 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0" data-id="element-7559">
              <StoreIcon size={32} className="text-gray-400" data-id="element-7560" />
            </div>
            <div data-id="element-7561">
              <div className="flex items-center gap-3" data-id="element-7562">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-7563">
                  {outlet.name}
                </h1>
                <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(outlet.status)}`} data-id="element-7564">
                  {outlet.status}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-500" data-id="element-7565">
                <span className="flex items-center gap-1" data-id="element-7566">
                  <span className="font-medium text-gray-700" data-id="element-7567">ID:</span>{' '}
                  {outlet.subMerchantId}
                </span>
                <span className="flex items-center gap-1" data-id="element-7568">
                  <MapPinIcon size={14} data-id="element-7569" />
                  {outlet.mall} • {outlet.floorZone}
                </span>
                <span className="flex items-center gap-1" data-id="element-7570">
                  <BuildingIcon size={14} data-id="element-7571" />
                  {outlet.category}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3" data-id="element-7572">
            <OutletDateRange data-id="element-7573" />
            <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm flex items-center gap-2" data-id="element-7574">
              <DownloadIcon size={16} data-id="element-7575" />
              {t('actions.export')}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6 overflow-x-auto hide-scrollbar" data-id="element-7576">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs" data-id="element-7577">
          {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`
                  group inline-flex items-center py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors
                  ${isActive ? 'border-[var(--brand-primary)] text-[var(--brand-primary)]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                `} data-id="element-7578">
                <Icon size={18} className={`mr-2 ${isActive ? 'text-[var(--brand-primary)]' : 'text-gray-400 group-hover:text-gray-500'}`} data-id="element-7579" />
                {tab.label}
              </button>;
        })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[400px]" data-id="element-7580">
        {activeTab === 'overview' && <div className="space-y-6" data-id="element-7581">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-7582">
              <KpiCard title="Period GMV" value={formatMoney(outlet.periodGmv)} icon={<ActivityIcon size={20} data-id="element-7584" />} trend={{
            value: `${outlet.growth}%`,
            isPositive: outlet.growth >= 0
          }} color="blue" data-id="element-7583" />
              <KpiCard title="Transactions" value={outlet.txnCount.toLocaleString()} icon={<CreditCardIcon size={20} data-id="element-7586" />} trend={{
            value: '5.2%',
            isPositive: true
          }} color="green" data-id="element-7585" />
              <KpiCard title="Average Ticket" value={formatMoney(outlet.avgTicket)} icon={<StoreIcon size={20} data-id="element-7588" />} color="purple" data-id="element-7587" />
              <KpiCard title="Net Settled" value={formatMoney(outlet.periodGmv * 0.98)} icon={<BuildingIcon size={20} data-id="element-7590" />} color="orange" data-id="element-7589" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-7591">
              <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-7592">
                <h3 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-7593">
                  Sales Over Time
                </h3>
                <div className="h-72" data-id="element-7594">
                  <ResponsiveContainer width="100%" height="100%" data-id="element-7595">
                    <LineChart data={mockSalesData} data-id="element-7596">
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-7597" />
                      <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                    fill: '#6B7280',
                    fontSize: 12
                  }} dy={10} data-id="element-7598" />
                      <YAxis axisLine={false} tickLine={false} tick={{
                    fill: '#6B7280',
                    fontSize: 12
                  }} tickFormatter={val => `RM${val / 1000}k`} dx={-10} data-id="element-7599" />
                      <Tooltip formatter={(value: number) => [formatMoney(value), 'Sales']} contentStyle={{
                    borderRadius: '8px',
                    border: 'none',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }} data-id="element-7600" />
                      <Line type="monotone" dataKey="sales" stroke="var(--brand-primary)" strokeWidth={3} dot={{
                    r: 4,
                    fill: 'var(--brand-primary)',
                    strokeWidth: 2,
                    stroke: '#fff'
                  }} activeDot={{
                    r: 6,
                    strokeWidth: 0
                  }} data-id="element-7601" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-7602">
                <h3 className="text-lg font-semibold text-gray-900 mb-6" data-id="element-7603">
                  Channel Mix
                </h3>
                <div className="space-y-4" data-id="element-7604">
                  {outlet.channels.includes('POS') && <div data-id="element-7605">
                      <div className="flex justify-between text-sm mb-1" data-id="element-7606">
                        <span className="flex items-center gap-2 text-gray-700" data-id="element-7607">
                          <CreditCardIcon size={16} className="text-gray-400" data-id="element-7608" />{' '}
                          POS (In-store)
                        </span>
                        <span className="font-medium text-gray-900" data-id="element-7609">65%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-7610">
                        <div className="bg-blue-500 h-2 rounded-full" style={{
                    width: '65%'
                  }} data-id="element-7611"></div>
                      </div>
                    </div>}
                  {outlet.channels.includes('APM') && <div data-id="element-7612">
                      <div className="flex justify-between text-sm mb-1" data-id="element-7613">
                        <span className="flex items-center gap-2 text-gray-700" data-id="element-7614">
                          <SmartphoneIcon size={16} className="text-gray-400" data-id="element-7615" />{' '}
                          APM (Wallets)
                        </span>
                        <span className="font-medium text-gray-900" data-id="element-7616">25%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-7617">
                        <div className="bg-green-500 h-2 rounded-full" style={{
                    width: '25%'
                  }} data-id="element-7618"></div>
                      </div>
                    </div>}
                  {outlet.channels.includes('E-com') && <div data-id="element-7619">
                      <div className="flex justify-between text-sm mb-1" data-id="element-7620">
                        <span className="flex items-center gap-2 text-gray-700" data-id="element-7621">
                          <GlobeIcon size={16} className="text-gray-400" data-id="element-7622" />{' '}
                          E-commerce
                        </span>
                        <span className="font-medium text-gray-900" data-id="element-7623">10%</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2" data-id="element-7624">
                        <div className="bg-purple-500 h-2 rounded-full" style={{
                    width: '10%'
                  }} data-id="element-7625"></div>
                      </div>
                    </div>}
                </div>

                <div className="mt-8" data-id="element-7626">
                  <h4 className="text-sm font-medium text-gray-900 mb-3" data-id="element-7627">
                    Top APMs
                  </h4>
                  <div className="space-y-3" data-id="element-7628">
                    <div className="flex justify-between items-center text-sm" data-id="element-7629">
                      <span className="text-gray-600" data-id="element-7630">Touch 'n Go</span>
                      <span className="font-medium" data-id="element-7631">42%</span>
                    </div>
                    <div className="flex justify-between items-center text-sm" data-id="element-7632">
                      <span className="text-gray-600" data-id="element-7633">DuitNow QR</span>
                      <span className="font-medium" data-id="element-7634">35%</span>
                    </div>
                    <div className="flex justify-between items-center text-sm" data-id="element-7635">
                      <span className="text-gray-600" data-id="element-7636">GrabPay</span>
                      <span className="font-medium" data-id="element-7637">15%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>}

        {activeTab === 'payments' && <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center" data-id="element-7638">
            <CreditCardIcon size={48} className="mx-auto text-gray-300 mb-4" data-id="element-7639" />
            <h3 className="text-lg font-medium text-gray-900" data-id="element-7640">
              Consolidated Ledger
            </h3>
            <p className="text-gray-500 mt-2 max-w-md mx-auto" data-id="element-7641">
              This view consolidates all transactions across POS, E-commerce,
              and APMs for this outlet.
            </p>
            <button className="mt-6 px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50" data-id="element-7642">
              View Transactions
            </button>
          </div>}

        {activeTab === 'channels' && <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center" data-id="element-7643">
            <SmartphoneIcon size={48} className="mx-auto text-gray-300 mb-4" data-id="element-7644" />
            <h3 className="text-lg font-medium text-gray-900" data-id="element-7645">
              Channel Breakdown
            </h3>
            <p className="text-gray-500 mt-2 max-w-md mx-auto" data-id="element-7646">
              Detailed metrics for POS terminals, online gateways, and
              Alternative Payment Methods.
            </p>
          </div>}

        {activeTab === 'reconciliation' && <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center" data-id="element-7647">
            <RefreshCwIcon size={48} className="mx-auto text-gray-300 mb-4" data-id="element-7648" />
            <h3 className="text-lg font-medium text-gray-900" data-id="element-7649">
              Reconciliation
            </h3>
            <p className="text-gray-500 mt-2 max-w-md mx-auto" data-id="element-7650">
              Match captured transactions to settlement and payout for this
              outlet.
            </p>
          </div>}

        {activeTab === 'reports' && <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center" data-id="element-7651">
            <FileTextIcon size={48} className="mx-auto text-gray-300 mb-4" data-id="element-7652" />
            <h3 className="text-lg font-medium text-gray-900" data-id="element-7653">
              Outlet Reports
            </h3>
            <p className="text-gray-500 mt-2 max-w-md mx-auto" data-id="element-7654">
              Generate performance reports, category benchmarks, and fee
              statements.
            </p>
          </div>}

        {activeTab === 'settings' && <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center" data-id="element-7655">
            <SettingsIcon size={48} className="mx-auto text-gray-300 mb-4" data-id="element-7656" />
            <h3 className="text-lg font-medium text-gray-900" data-id="element-7657">
              Outlet Settings
            </h3>
            <p className="text-gray-500 mt-2 max-w-md mx-auto" data-id="element-7658">
              Manage profile, settlement accounts, enabled payment methods, and
              user access.
            </p>
          </div>}
      </div>
    </div>;
}